/**
 * PSTUDIO-4168: the one place that builds the store's solution-version identity
 * from XState context.
 *
 * The shape (`<customerId>-<botName>-<draftVersion>`) is load-bearing: it is the
 * value `tableStore.solutionVersionId` holds, what every queued write is stamped
 * with, what `solutionScopeOf` strips a version off, and what the cross-tab
 * broadcast is filtered by. It was already spelled out in two places
 * (`use-table-store-sync`, `builder`) and this ticket needed a third reader, so
 * it lives here instead — a drift between two copies of THIS string is a write
 * landing in the wrong solution.
 *
 * Returns `''` when any part is missing, which is the value the store uses for
 * "outside the builder" and which every write path already fails safe on.
 */
export function buildSolutionVersionId(parts: {
  customerId?: string | null;
  botName?: string | null;
  draftVersion?: string | null;
}): string {
  const { customerId, botName, draftVersion } = parts;
  if (!customerId || !botName || !draftVersion) {
    return '';
  }
  return `${customerId}-${botName}-${draftVersion}`;
}

const DRAFT_VERSION_SUFFIX = /-v\d+$/;

/**
 * PSTUDIO-3953: the solution part of a dash-joined solution-version id
 * (`<customer>-<bot>-<version>` — see `downloadVersionFile`, which documents
 * the same shape).
 *
 * Used to tell the two identity changes apart, because they need opposite
 * handling on the write path:
 *   - same scope, different version (a publish bumps `v3` -> `v4`): the queued
 *     rows belong in the NEW version, so PSTUDIO-3669's flush-time re-home is
 *     correct and must be preserved;
 *   - different scope (the user switched solutions): re-homing would write one
 *     solution's rows into another, so the queue is drained first.
 *
 * Only a trailing segment that LOOKS like a draft version (`-v<digits>`, the
 * shape `load-bot-versions` produces) is stripped. Cutting at the last dash
 * unconditionally was the first attempt and it is wrong in both directions as
 * soon as a version is not of that shape: a version carrying a dash would make
 * two versions of the SAME solution compare as different solutions (draining
 * instead of re-homing — a PSTUDIO-3669 regression), and it lets `bot=x` +
 * `ver=y-1` collide with `bot=x-y` + `ver=9`. Anything that does not end in a
 * recognisable version is returned whole. That trade is deliberate but it is not
 * free in both directions: for a version that carries NO `v` prefix (say `1`)
 * the old last-dash cut classified a bump correctly, whereas this returns two
 * different scopes and drains instead of re-homing — a PSTUDIO-3669-shaped
 * regression. It is chosen anyway because the failure it prevents (re-homing
 * rows into the WRONG BOT) loses data, while the one it can cause (committing
 * to the version being left) does not. Every version this codebase produces is
 * `v<digits>` (`load-bot-versions`), so the lossy direction is unreachable
 * today; if that ever changes, widen the pattern rather than reverting.
 *
 * A bot name containing dashes is fine — both ids are reduced the same way.
 */
export const solutionScopeOf = (solutionVersionId: string): string =>
  solutionVersionId.replace(DRAFT_VERSION_SUFFIX, '');
